<template>
  <div v-if="encendido" class="turnstile-widget" data-test="turnstile">
    <div ref="caja" class="turnstile-widget__caja" />
    <p v-if="fallo" class="turnstile-widget__fallo" role="alert">{{ $t('turnstile.failed') }}</p>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { TURNSTILE_SITE_KEY, turnstileEncendido, cargarTurnstile, type ApiTurnstile } from '@/services/turnstile';

/**
 * El widget de Turnstile y nada más: entrega su token al formulario con
 * `@token` (string al resolverse, null al caducar o fallar).
 *
 * Un token vale UNA vez: el formulario llama a `reset()` tras cada envío, salga
 * bien o mal, para que el siguiente intento lleve uno nuevo.
 *
 * Sin `VUE_APP_TURNSTILE_SITE_KEY` no pinta nada ni carga ningún script.
 */
export default defineComponent({
  name: 'TurnstileWidget',
  data() {
    return {
      encendido: turnstileEncendido,
      fallo: false,
      widgetId: null as string | null,
      api: null as ApiTurnstile | null,
    };
  },
  async mounted(): Promise<void> {
    if (!this.encendido) return;
    try {
      const api = await cargarTurnstile();
      const caja = this.$refs.caja as HTMLElement | undefined;
      if (!caja) return;
      this.api = api;
      this.widgetId = api.render(caja, {
        sitekey: TURNSTILE_SITE_KEY,
        language: String(this.$i18n?.locale || 'es'),
        callback: (token: string) => {
          this.fallo = false;
          this.$emit('token', token);
        },
        'expired-callback': () => this.$emit('token', null),
        'error-callback': () => {
          this.fallo = true;
          this.$emit('token', null);
        },
      });
    } catch {
      this.fallo = true;
      this.$emit('token', null);
    }
  },
  beforeDestroy(): void {
    if (this.api && this.widgetId) {
      try { this.api.remove(this.widgetId); } catch { /* el widget ya no está */ }
    }
  },
  methods: {
    /** Pide un token nuevo: el anterior ya se gastó (o caducó). */
    reset(): void {
      if (!this.api || !this.widgetId) return;
      this.$emit('token', null);
      try { this.api.reset(this.widgetId); } catch { this.fallo = true; }
    },
  },
});
</script>

<style scoped>
.turnstile-widget { margin: var(--casa-espacio-1) 0 var(--casa-espacio-3); }
.turnstile-widget__caja { min-height: 65px; }
.turnstile-widget__fallo {
  margin: var(--casa-espacio-1-5) 0 0;
  font-size: var(--casa-letra-control);
  color: var(--casa-error);
}
</style>
